import { ReactNode } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { CustomPageLayout } from '../common';
import { BreadcrumbItem } from '../../types';

interface EntityPageLayoutProps {
  children: ReactNode;
}

interface EntityUserProfile {
  displayName?: string;
  email?: string;
  picture?: string;
}

/**
 * Wrapper component that provides CustomPageLayout with dynamic
 * breadcrumbs and title based on the current entity
 */
export const EntityPageLayout = ({ children }: EntityPageLayoutProps) => {
  const { entity } = useEntity();

  // Generate breadcrumbs based on entity kind and type
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Catalog', path: '/catalog' },
  ];

  let displayName = undefined;

  // Add kind-specific breadcrumb
  const kind = entity.kind.toLowerCase();
  if (kind === 'component') {
    breadcrumbs.push({
      label: 'Components',
      path: '/catalog?filters[kind]=component',
    });
  } else if (kind === 'api') {
    breadcrumbs.push({
      label: 'APIs',
      path: '/catalog?filters[kind]=api',
    });
  } else if (kind === 'group') {
    breadcrumbs.push({
      label: 'Groups',
      path: '/catalog?filters[kind]=group',
    });
  } else if (kind === 'user') {
    breadcrumbs.push({
      label: 'Users',
      path: '/catalog?filters[kind]=user',
    });
    const profile = entity.spec?.profile as EntityUserProfile | undefined;
    if (profile?.displayName) {
      displayName = profile.displayName;
    }
  } else if (kind === 'system') {
    breadcrumbs.push({
      label: 'Systems',
      path: '/catalog?filters[kind]=system',
    });
  } else if (kind === 'domain') {
    breadcrumbs.push({
      label: 'Domains',
      path: '/catalog?filters[kind]=domain',
    });
  }

  breadcrumbs.push({
    label: displayName || entity.metadata.title || entity.metadata.name,
  });

  const title = displayName || entity.metadata.title || entity.metadata.name;
  const description = entity.metadata.description;

  return (
    <CustomPageLayout
      title={title}
      description={description}
      breadcrumbs={breadcrumbs}
      showDate
    >
      {children}
    </CustomPageLayout>
  );
};
